import { Persistore } from 'persistore';
import { _ } from '@hugendubel/utils';
import { Type, WsAvailabilityData, Money, createCall, VoidParser, Method } from '@hugendubel/ws_client';

const SESSION_ID_KEY = 'prudsysSessionId';
const get = () => {
    const current = Persistore.get(SESSION_ID_KEY);
    if (current)
        return current;
    const generated = _.uuid();
    Persistore.set(SESSION_ID_KEY, generated);
    return generated;
};
const refresh = () => Persistore.remove(SESSION_ID_KEY);
const SessionIdStore = { get, refresh };

const parseOldPrice = (oldPrice) => {
    const parsedOldPrice = Type.optional.number(oldPrice, 'ArticleListEntry.oldPrice');
    return parsedOldPrice ? Money.parse(parsedOldPrice) : undefined;
};
const parsePrice = (price) => {
    const parsedPrice = Type.optional.number(price, 'ArticleListEntry.price');
    return parsedPrice ? Money.parse(parsedPrice) : undefined;
};
const parse$1 = (json) => ({
    articleId: Type.id(json.articleId, 'ArticleListEntry.articleId'),
    authors: Type.optional.string(json.authors, 'ArticleListEntry.authors'),
    availabilityData: WsAvailabilityData.parse(Type.object(json.availabilityData, 'ArticleListEntry.availabilityData')),
    availabilityNumber: Type.optional.number(json.availabilityNumber, 'ArticleListEntry.availabilityNumber'),
    binder: Type.optional.string(json.binder, 'ArticleListEntry.binder'),
    binderTypeId: Type.optional.number(json.binderTypeId, 'ArticleListEntry.binderTypeId'),
    bonusCost: Type.optional.number(json.bonusCost, 'ArticleListEntry.bonusCost'),
    cdpPublicationId: Type.optional.string(json.cdpPublicationId, 'ArticleListEntry.cdpPublicationId'),
    coverImagePath: Type.optional.string(json.coverImagePath, 'ArticleListEntry.coverImagePath'),
    coverImageWidth: Type.optional.number(json.coverImageWidth, 'ArticleListEntry.coverImageWidth'),
    coverExtraLargeImagePath: Type.optional.string(json.coverExtraLargeImagePath, 'ArticleListEntry.coverExtraLargeImagePath'),
    coverExtraLargeWidth: Type.optional.number(json.coverExtraLargeWidth, 'ArticleListEntry.coverExtraLargeWidth'),
    coverLargeImagePath: Type.optional.string(json.coverLargeImagePath, 'ArticleListEntry.coverLargeImagePath'),
    coverLargeWidth: Type.optional.number(json.coverLargeWidth, 'ArticleListEntry.coverLargeWidth'),
    download: Type.boolean(json.download, 'ArticleListEntry.download'),
    ean: Type.optional.string(json.ean, 'ArticleListEntry.ean'),
    oldPrice: parseOldPrice(json.oldPrice),
    oldPriceText: Type.optional.string(json.oldPriceText, 'ArticleListEntry.oldPriceText'),
    pickupPossible: Type.boolean(json.pickupPossible, 'ArticleListEntry.pickupPossible'),
    price: parsePrice(json.price),
    priceText: Type.optional.string(json.priceText, 'ArticleListEntry.priceText'),
    productTypeName: Type.optional.string(json.productTypeName, 'ArticleListEntry.productTypeName'),
    productTypeDescription: Type.optional.string(json.productTypeDescription, 'ArticleListEntry.productTypeDescription'),
    rebateCategory: Type.optional.number(json.rebateCategory, 'ArticleListEntry.rebateCategory'),
    reviewAvg: Type.optional.number(json.reviewAvg, 'ArticleListEntry.reviewAvg'),
    title: Type.optional.string(json.title, 'ArticleListEntry.title'),
    trackingToken: Type.optional.string(json.trackingToken, 'ArticleListEntry.trackingToken'),
});
const WsArticleListEntry = { parse: parse$1 };

const _parseArticleListEntries = (json, name) => (Type.optional.array(json, name) || []).map(WsArticleListEntry.parse);
const parse = (json) => ({
    products: _parseArticleListEntries(json.products, 'RecommendationsResult.products'),
    personals: _parseArticleListEntries(json.personals, 'RecommendationsResult.personals'),
    lastProducts: _parseArticleListEntries(json.lastProducts, 'RecommendationsResult.lastProducts'),
    fallbackProducts: _parseArticleListEntries(json.fallbackProducts, 'RecommendationsResult.fallbackProducts'),
    lastProductsTitle: Type.optional.string(json.lastProductsTitle, 'RecommendationsResult.lastProductsTitle'),
    personalTitle: Type.optional.string(json.personalTitle, 'RecommendationsResult.personalTitle'),
    productsTitle: Type.optional.string(json.productsTitle, 'RecommendationsResult.productsTitle'),
});
const WsRecommendationsResult = { parse };

const WS = {
    prudsys: {
        event: createCall({ name: 'prudsys/event', parser: VoidParser, method: Method.POST, withCookies: true }),
        recommendation: createCall({
            name: 'prudsys/recommendation',
            parser: WsRecommendationsResult.parse,
            method: Method.POST,
            withCookies: true,
        }),
    },
};

const _Dispatcher = {
    NOP: () => { },
};
const basket = (articles) => {
    const articleIds = articles.map(a => a.articleId);
    const quantities = articles.map(a => a.amount);
    return WS.prudsys
        .event({
        sid: SessionIdStore.get(),
        pids: articleIds,
        quantities: quantities,
        eventType: 'B',
    })
        .catch(_Dispatcher.NOP);
};
const click = (articleId, trackingToken) => WS.prudsys
    .event({
    sid: SessionIdStore.get(),
    pid: articleId,
    trackingtoken: trackingToken,
    eventType: 'C',
})
    .catch(_Dispatcher.NOP);
const order = (articles) => {
    const articleIds = articles.map(a => a.articleId);
    const quantities = articles.map(a => a.amount);
    const totals = articles.map(a => a.price * a.amount);
    return WS.prudsys
        .event({
        sid: SessionIdStore.get(),
        pids: articleIds,
        totals,
        quantities,
        eventType: 'O',
    })
        .catch(_Dispatcher.NOP);
};
const productView = (articleId) => WS.prudsys
    .event({
    sid: SessionIdStore.get(),
    pid: articleId,
    eventType: 'P',
})
    .catch(_Dispatcher.NOP);
const Dispatcher = { basket, click, order, productView };

const FALLBACK = { products: [], personals: [], lastProducts: [], fallbackProducts: [] };
const recommendations = (recommendationType, applicationType, articleId, textBlockId) => WS.prudsys
    .recommendation({
    recommendationType,
    applicationType,
    sid: SessionIdStore.get(),
    pid: articleId,
    tid: textBlockId,
})
    .catch(() => FALLBACK);
const Prudsys = { recommendations, Dispatcher };

export { FALLBACK, Prudsys, SessionIdStore, WsArticleListEntry, WsRecommendationsResult };
